import { createContext, useContext, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/shared";
import { LogOut, Menu, X, BellRing } from "lucide-react";

const ShellTheme = createContext("light");
export const useShellTheme = () => useContext(ShellTheme);

const GREEN = "#35A787";
const GREEN_DEEP = "#2D8F74";
const GREEN_SOFT = "#E8F6F1";
const INK = "#14130E";
const MUTED = "#6B6659";
const BORDER = "#EAE9E4";
const NAV_OFF = "#5B5A56";
const SIDEBAR = "#FFFFFF";

export function Shell({ accent = GREEN, portalName, nav, active, onNav, children, theme = "light", bell, extra }) {
  const { user, logout, config } = useAuth();
  const [open, setOpen] = useState(false);
  const dark = theme === "dark";

  useEffect(() => {
    if (dark) document.body.style.background = "#0C0A09";
    return () => { document.body.style.background = ""; };
  }, [dark]);

  const NavList = ({ onClick }) => (
    <nav className="flex flex-col gap-1">
      {nav.map((n) => {
        const on = active === n.key;
        return (
          <button key={n.key} data-testid={`nav-${n.key}`}
            onClick={() => { onNav(n.key); onClick && onClick(); }}
            className={cn(
              "tap group relative flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-[13.5px] transition-all duration-150",
              on ? "bg-[#E8F6F1] font-semibold text-[#2D8F74]" : "text-[#5B5A56] hover:bg-[#F4F4F2] hover:text-[#14130E]"
            )}>
            {on && <span className="absolute inset-y-2.5 left-0 w-[3px] rounded-r-full" style={{ background: GREEN }} />}
            <n.icon size={18} className="shrink-0" strokeWidth={on ? 2.3 : 1.9} style={on ? { color: GREEN } : {}} />
            <span className="truncate">{n.label}</span>
            {n.badge ? (
              <span className="ml-auto flex h-5 min-w-[18px] items-center justify-center rounded-full bg-[#35A787] px-1.5 text-[10px] font-bold text-white shadow-sm">{n.badge}</span>
            ) : null}
          </button>
        );
      })}
    </nav>
  );

  const BellAction = bell ? (
    <button data-testid="ops-notification-bell" onClick={bell.onClick}
      className="tap relative flex h-9 w-9 flex-none items-center justify-center rounded-lg border border-[#E5E7EB] bg-white text-[#1F2937] transition-colors hover:bg-[#F4F4F2]">
      <BellRing size={16} />
      {(bell.count || 0) > 0 && (
        <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#D64545] px-1 text-[9px] font-bold text-white shadow">{bell.count}</span>
      )}
    </button>
  ) : null;

  const SidebarButtons = () => (
    <div className="flex items-center gap-2">
      {extra || null}
      {BellAction}
    </div>
  );

  const SidebarProfile = () => {
    const initial = (user?.name || "?").trim().charAt(0).toUpperCase();
    return (
      <div className="mt-auto rounded-xl border border-[#E5E7EB] p-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#E8F6F1] text-sm font-bold text-[#2D8F74]">{initial}</span>
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold text-[#14130E]">{user?.name}</div>
            <div className="truncate text-[11px] text-[#8A887F]">{user?.email || user?.phone}</div>
          </div>
        </div>
        <button data-testid="logout-btn" onClick={logout}
          className="tap mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-medium text-[#6B6659] transition-colors hover:bg-[#F4F4F2] hover:text-[#14130E]">
          <LogOut size={13} /> Sign out
        </button>
      </div>
    );
  };

  const LogoBlock = (size) => (
    <button data-testid="logo-home-btn" onClick={() => onNav(nav[0]?.key)}
      className="tap flex cursor-pointer items-center gap-2.5 text-left">
      <Logo size={size || 30} className="max-w-[120px] object-contain" />
      <div className="leading-tight">
        <div className="font-display text-[15px] font-bold tracking-tight text-[#14130E]">{config?.brand?.name || "Anand"}</div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#35A787]">{portalName}</div>
      </div>
    </button>
  );

  return (
    <ShellTheme.Provider value={theme}>
    <div className="min-h-screen text-[#1F2937]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-[#E5E7EB] p-4 pr-5 lg:flex"
        style={{ background: SIDEBAR }}>
        <div className="flex h-10 items-center gap-2.5 px-1">
          {LogoBlock()}
          <div className="ml-auto">{SidebarButtons()}</div>
        </div>
        <div className="mt-8 flex-1 overflow-y-auto pr-1"><NavList /></div>
        <SidebarProfile />
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-[#E5E7EB] bg-white px-4 py-3 lg:hidden">
        <button className="tap text-[#1F2937]" onClick={() => setOpen(true)}><Menu size={20} /></button>
        {LogoBlock()}
        <div className="ml-auto">{SidebarButtons()}</div>
      </header>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="animate-in absolute inset-y-0 left-0 flex w-72 flex-col border-r border-[#E5E7EB] bg-white p-4"
            style={{ background: SIDEBAR, borderRadius: "0 1.25rem 1.25rem 0" }}>
            <div className="mb-5 flex items-center justify-between">
              {LogoBlock()}
              <button className="tap text-[#1F2937]" onClick={() => setOpen(false)}><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto"><NavList onClick={() => setOpen(false)} /></div>
            <div className="mt-4"><SidebarProfile /></div>
          </div>
        </div>
      )}

      <main className="lg:pl-64">
        <div className="mx-auto max-w-[1240px] px-4 py-6 sm:px-8 sm:py-8">
          <motion.div key={active} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, ease: [0.2, 0.7, 0.2, 1] }}>
            {children}
          </motion.div>
        </div>
      </main>
    </div>
    </ShellTheme.Provider>
  );
}

export function PageHead({ title, sub, action }) {
  const dark = useShellTheme() === "dark";
  return (
    <div className="mb-10 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className={cn("font-display text-4xl font-bold leading-tight tracking-tight", dark ? "text-white" : "text-[#14130E]")}>{title}</h1>
        {sub && <p className={cn("mt-2 text-base", dark ? "text-slate-400" : "text-[#6B6659]")}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}

export function Card({ children, className, ...rest }) {
  const dark = useShellTheme() === "dark";
  return <div className={cn("ent-card p-5", dark && "!bg-slate-900 !border-slate-800", className)} {...rest}>{children}</div>;
}
